import React, { useCallback, useEffect, useState } from "react";
import { Tile } from "components/UIComponents/Tile";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import styles from "./DocumentList.module.scss";
import {
  getContactDocumentsList,
  getClientDocumentsList,
  getSupplierDocumentsList,
  getDocument,
  updateContactDocumentItem,
  updateClientDocumentItem,
  updateSupplierDocumentItem,
  downloadDocument,
  downloadDocumentAsPDF,
  getDocumentEditor,
  saveDocumentEditor,
} from "api";
import {
  downloadBlobAsFile,
  parseDownloadFilename,
} from "components/common/fileDownload";
import { DateFormatCell } from "components/common/formatter";
import { Button } from "@progress/kendo-react-buttons";
import { process } from "@progress/kendo-data-query";
import ClientDocumentModal from "./AddEditDocument";
import fileIcon from "components/common/fileIcon";
import { FileDragDrop } from "components/Activities/FileDragDrop";
import DocumentViewer from "components/common/DocumentViewer";
import ConfirmationDialog from "components/common/ConfirmationDialog";
import { RxCross2 } from "react-icons/rx";
import { AiOutlineFileText } from "react-icons/ai";
import { SvgIcon } from "@progress/kendo-react-common";
import { arrowRotateCwIcon as refreshIcon } from "@progress/kendo-svg-icons";
import SearchTextBox from "components/SearchComponent";
import { toast } from "react-toastify";

const documentRegisterId = (row, component) => {
  if (!row) return undefined;
  if (component === "supplier") {
    return row?.supplierDocumentID;
  }
  if (component === "contact") {
    return row?.contactDocumentID
  }
  return row?.clientDocumentID;
};

export const DocumentList = ({
  clientId,
  supplierId,
  supplierRef,
  contactId,
  tileHeading,
  viewMode,
  component,
  canModify = false,
  className = "",
}) => {
  const isSupplier = supplierId != null;
  const isContact = component === "contact";
  const heading =
    tileHeading ?? (isSupplier ? "Documents" : "Document Register");
  const [list, setLists] = useState({ data: [], total: 50 });
  const [showDocRegister, setShowDocRegister] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [viewId, setViewId] = useState();
  const uploadRef = React.createRef();
  const [showDocument, setShowDocument] = useState(false);
  const [documentViewerData, setDocumentViewerData] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docRowToDelete, setDocRowToDelete] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const fetchDocs = useCallback(async () => {
    try {
      if (supplierId != null) {
        const data = await getSupplierDocumentsList(supplierId);
        const rows = data?.data?.data ?? [];
        setLists({ data: rows, total: rows.length });
      } else if (isContact && contactId) {
        const data = await getContactDocumentsList(contactId, searchValue);
        const rows = data?.data?.data ?? [];
        setLists({ data: rows, total: rows.length });
      } else if (clientId) {
        const data = await getClientDocumentsList(clientId?.clientId);
        const rows = data?.data?.data ?? [];
        setLists({ data: rows, total: rows.length });
      }
    } catch (error) { }
  }, [clientId, supplierId, contactId, isContact, searchValue]);

  const [dataState, setDataState] = React.useState({
    take: 100,
    skip: 0,
    sort: [
      {
        field: "documentDate",
        dir: "asc",
      },
    ],
  });

  const showDocumentFile = async (id) => {
    const { data } = await getDocument(id);
    setDocumentViewerData([data.data]);
    setShowDocument(true);
  };

  const handleDownload = async (documentId, asPDF = false) => {
    if (!documentId) return;
    try {
      const response = asPDF
        ? await downloadDocumentAsPDF(documentId)
        : await downloadDocument(documentId);
      const fileName = parseDownloadFilename(response, asPDF);
      downloadBlobAsFile(response.data, fileName);
    } catch (error) { }
  };

  const handleEditDoc = async (id, type) => {
    if (!id) return;
    const matterId = documentViewerData[0]?.matterId ?? id;
    try {
      const { data } = await getDocumentEditor(id, matterId);
      let editUrl = data?.data?.editURL;
      if (type === "Local") {
        const docName = documentViewerData[0]?.documentName ?? "";
        const ext = docName.split(".").pop().toLowerCase();
        editUrl = ["xls", "xlsx"].includes(ext)
          ? `ms-excel:ofe|u|${editUrl}`
          : `ms-word:ofe|u|${editUrl}`;
      }
      window.open(editUrl, "_blank");
      showDocumentFile(id);
    } catch (error) { }
  };

  const handleSaveDocumentEdit = async (id) => {
    if (!id) return;
    const matterId = documentViewerData[0]?.matterId ?? id;
    try {
      await saveDocumentEditor(id, matterId);
      toast.success("Document Saved");
      showDocumentFile(id);
    } catch (error) { }
  };

  const ViewDocs = (props) => {
    return (
      <td className={styles["command-cell"]}>
        <Button
          onClick={() => {
            setViewId(props.dataItem);
            setShowDocRegister(true);
          }}
          className="button-small"
        >
          View
        </Button>
      </td>
    );
  };

  const openDocumentInNewTab = (documentId) => {
    if (!documentId) return;
    const encodedId = btoa(JSON.stringify({ id: documentId }));
    window.open(`/sa/document-viewer?id=${encodedId}`, "_blank");
  };

  const OpenDocInNewTab = (props) => {
    const { documentId } = props.dataItem;
    if (!documentId) {
      return <td />;
    }
    return (
      <td className={`text-center ${styles["command-cell"]}`}>
        <Button
          className="btn m-0 p-0 border-0"
          title="Open document in a new tab"
          aria-label="Open document in a new tab"
          onClick={(e) => {
            e.stopPropagation();
            openDocumentInNewTab(documentId);
          }}
        >
          <AiOutlineFileText size={20} color="#3B82F6" aria-hidden />
        </Button>
      </td>
    );
  };

  const handleDeleteClick = (dataItem) => {
    const regId = documentRegisterId(dataItem, component);
    if (!regId) return;
    setDocRowToDelete(dataItem);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteDocument = async () => {
    const regId = documentRegisterId(docRowToDelete, component);
    if (!regId) {
      setShowDeleteConfirm(false);
      setDocRowToDelete(null);
      return;
    }
    setDeletingDoc(true);
    try {
      const payload = { data: { active: false } };
      if (isSupplier) {
        await updateSupplierDocumentItem(payload, supplierId, regId);
      } else if (isContact) {
        await updateContactDocumentItem(payload, contactId, regId);
      } else {
        await updateClientDocumentItem(payload, clientId?.clientId, regId);
      }
      toast.success("Document removed");
      await fetchDocs();
    } catch (err) {
      console.error(err);
    } finally {
      setShowDeleteConfirm(false);
      setDocRowToDelete(null);
      setDeletingDoc(false);
    }
  };

  const DeleteDocCell = (props) => {
    const regId = documentRegisterId(props.dataItem, component);
    if (!regId) {
      return <td />;
    }
    return (
      <td className={`text-center ${styles["command-cell"]}`}>
        <Button
          className="btn m-0 p-0 border-0"
          title="Remove document"
          disabled={component === "supplier" ? false : !canModify}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(props.dataItem);
          }}
        >
          <RxCross2 size={20} color="red" aria-hidden />
        </Button>
      </td>
    );
  };

  const DocumentCell = (e) => {
    const { documentId, documentName } = e.dataItem;
    return documentId ? (
      <td className="p-1" {...e} onClick={() => showDocumentFile(documentId)}>
        <div
          role="button"
          className={`d-flex align-items-center ${styles["document-name-cell"]}`}
          title={documentName}
        >
          {fileIcon(documentName)}

          <span
            className={`ps-2 fs-sm d-lg-none d-xl-block text-truncate ${styles["document-name"]}`}
          >
            {documentName}
          </span>

          <span className="ps-2 fs-sm d-xl-none d-lg-block">
            {documentName?.split(".").pop()}
          </span>
        </div>
      </td>
    ) : (
      <td></td>
    );
  };

  const dataStateChange = (e) => {
    if (
      e.dataState.sort !== undefined &&
      e.dataState.skip !== 0 &&
      dataState.sort !== e.dataState.sort
    ) {
      setDataState({ ...e.dataState, skip: 0 });
    } else if (e.dataState?.sort?.[0] === undefined) {
      setDataState({
        ...e.dataState,
        sort: [
          {
            field: dataState?.sort?.[0].field,
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState(e.dataState);
    }
  };

  const handleDrop = (event) => {
    setDroppedFiles([...event]);
    setShowDocRegister(true);
  };

  useEffect(() => {
    if (supplierId != null || clientId || (isContact && contactId)) {
      fetchDocs();
    }
  }, [fetchDocs, clientId, supplierId, isContact, contactId]);

  const fillsDialogTab = component === "client";
  const gridHeightClass =
    component === "supplier" ? styles.supplierGrid : styles.clientGrid;

  return (
    <div className={className}>
      <Tile
        heading={heading}
        className={
          fillsDialogTab
            ? "py-2 px-4 d-flex flex-column overflow-hidden dialog-tab-height"
            : "py-2 px-4 overflow-auto"
        }
        headingItems={
          <div className="d-flex align-items-center gap-2">
            {isContact && (
              <>
                <SearchTextBox
                  width="240px"
                  height="36px"
                  changeFilterValue={(value) => setSearchValue(value ?? "")}
                />
                <Button
                  className={`border-0 common-btn ${styles["refresh-button"]}`}
                  title="Refresh"
                  aria-label="Refresh documents"
                  onClick={fetchDocs}
                >
                  <SvgIcon icon={refreshIcon} />
                </Button>
              </>
            )}
            <Button
              className="common-btn bgBtnSecondary fw-bold me-1"
              disabled={component === "supplier" ? false : !canModify}
              onClick={() => {
                setViewId(null);
                setShowDocRegister(true);
              }}
            >
              Add Document
            </Button>
          </div>
        }
      >
        <div className="flex-grow-1" style={{ minHeight: 0 }}>
          <FileDragDrop onFilesDrop={handleDrop} uploadRef={uploadRef}>
            <Grid
              data={process(list.data || [], dataState)}
              onDataStateChange={dataStateChange}
              className={`fs-md pb-1 ${fillsDialogTab ? "" : gridHeightClass}`}
              style={fillsDialogTab ? { height: "100%" } : undefined}
              sortable={true}
              resizable={true}
              {...dataState}
            >
              <GridColumn
                field="date"
                title="Date"
                width="140px"
                cell={DateFormatCell}
                className="gridBoldField ps-4"
                headerClassName="ps-4"
              />
              <GridColumn field="details" title="Details" />
              <GridColumn field="documentTypeName" title="Type" width="130px" />
              <GridColumn
                field="documentId"
                title="Document"
                cell={DocumentCell}
                width="180px"
              />
              <GridColumn field="" title="  " width="85px" cell={ViewDocs} />
              {isContact && (
                <GridColumn
                  field=""
                  title=" "
                  width="32px"
                  cell={OpenDocInNewTab}
                  headerClassName="text-center"
                />
              )}
              <GridColumn
                field=""
                title=" "
                width="35px"
                cell={DeleteDocCell}
                headerClassName="text-center"
              />
            </Grid>
          </FileDragDrop>
        </div>
      </Tile>

      {showDocRegister && (
        <ClientDocumentModal
          setshowMatterForm={() => {
            setViewId(null);
            setShowDocRegister(false);
            if (droppedFiles.length) setDroppedFiles([]);
          }}
          droppedFiles={droppedFiles}
          docId={viewId}
          updateData={() => {
            setViewId(null);
            setShowDocRegister(false);
            if (droppedFiles.length) setDroppedFiles([]);
            fetchDocs();
          }}
          clientId={isSupplier ? undefined : clientId}
          supplierId={isSupplier ? supplierId : undefined}
          supplierRef={isSupplier ? supplierRef : undefined}
          contactId={isContact ? contactId : undefined}
          component={component}
          canModify={canModify}
        />
      )}

      {showDocument && (
        <DocumentViewer
          data={documentViewerData}
          onDownload={handleDownload}
          onEditDoc={handleEditDoc}
          saveDocuemntEdit={handleSaveDocumentEdit}
          onClose={() => {
            setShowDocument(false);
            setDocumentViewerData([]);
          }}
          share={false}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmationDialog
          content={`Are you sure you want to remove this document from the ${component === "supplier"
            ? "Supplier"
            : component === "contact"
              ? "Contact"
              : "Client"
            }?`}
          onClickYes={confirmDeleteDocument}
          onClickNo={() => {
            setShowDeleteConfirm(false);
            setDocRowToDelete(null);
          }}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDocRowToDelete(null);
          }}
          isLoading={deletingDoc}
        />
      )}
    </div>
  );
};
